import { useId } from "react"

import { Button, Flex, Form, Input, Modal } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CopyButton } from "@/molecules/CopyButton"
import { FooterModal } from "@/molecules/FooterModal"

import { CloudConnectionFields } from "./CloudConnectionFields"

import styles from "./CloudConnectionStatus.module.scss"

import type { CloudConnectionSettingsModalProps } from "."

// A connection's settings, edited in place as the webhook modal edits its
// endpoint: what Kodem reaches it as, read-only, then the settings themselves.
// Disconnecting is apart from saving, at the other end of the footer.
export const CloudConnectionSettingsModal = (
  props: CloudConnectionSettingsModalProps
) => {
  const { name, identity, children, onClose, onDisconnect } = props
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection"
  })
  const { t: tRoot } = useTranslation()
  const id = useId()

  return (
    <Modal
      open
      width={760}
      title={t("settingsOf", { name })}
      // A save in flight finishes before the settings close
      closable={!props.isSaving}
      maskClosable={!props.isSaving}
      onCancel={onClose}
      footer={FooterModal(
        <Button
          type="primary"
          loading={props.isSaving}
          disabled={props.saveDisabled}
          onClick={props.onSave}
        >
          {tRoot("general.save")}
        </Button>,
        {
          Aside: (
            <Button danger disabled={props.isSaving} onClick={onDisconnect}>
              {tRoot("general.disconnect")}
            </Button>
          )
        }
      )}
    >
      <Flex vertical gap={16} className={styles.settingsBody}>
        <CloudConnectionFields>
          {identity.map(({ label, value }, index) => (
            <Form.Item
              key={label}
              label={label}
              htmlFor={`${id}-identity-${index}`}
            >
              <Input
                id={`${id}-identity-${index}`}
                disabled
                value={value}
                addonAfter={
                  <CopyButton
                    type="link"
                    text={value || ""}
                    disabled={!value}
                  />
                }
              />
            </Form.Item>
          ))}
        </CloudConnectionFields>
        {children}
      </Flex>
    </Modal>
  )
}
